import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Calculator, Hotel, AlertTriangle, CheckCircle, Clock, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManDayCalculator = ({ 
  roomCount: initialRoomCount = "", 
  onCalculationComplete,
  triggerButton,
  showAsCard = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [roomCount, setRoomCount] = useState(initialRoomCount);
  const [riskLevel, setRiskLevel] = useState("low_risk");
  const [riskFactors, setRiskFactors] = useState([]);
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFactors, setLoadingFactors] = useState(true);

  // Fetch risk factors on mount
  useEffect(() => {
    const fetchRiskFactors = async () => {
      try {
        const response = await axios.get(`${API}/royalcert/risk-factors`, { 
          withCredentials: true 
        });
        setRiskFactors(response.data.risk_factors || []);
      } catch (error) {
        console.error("Risk faktörleri yüklenemedi:", error);
        toast.error("Risk faktörleri yüklenemedi");
      } finally {
        setLoadingFactors(false);
      }
    };
    fetchRiskFactors();
  }, []);

  const calculateManDay = async () => {
    if (!roomCount || roomCount <= 0) {
      toast.error("Geçerli bir oda sayısı girin");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/royalcert/calculate-manday`,
        {
          room_count: parseInt(roomCount),
          risk_level: riskLevel,
          selected_risk_factors: selectedFactors
        },
        { withCredentials: true }
      );
      
      setCalculationResult(response.data);
      
      if (onCalculationComplete) {
        onCalculationComplete(response.data);
      }
      
      toast.success("Adam-gün hesaplaması tamamlandı");
    } catch (error) {
      console.error("Hesaplama hatası:", error);
      toast.error("Hesaplama yapılamadı");
    } finally {
      setLoading(false);
    }
  };

  const toggleFactor = (factorId) => {
    setSelectedFactors(prev => 
      prev.includes(factorId) 
        ? prev.filter(id => id !== factorId)
        : [...prev, factorId]
    );
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      "high_risk": "Yüksek Risk",
      "low_risk": "Düşük Risk",
      "extremely_low_risk": "Çok Düşük Risk"
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      "high_risk": "bg-red-100 text-red-800 border-red-300",
      "low_risk": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "extremely_low_risk": "bg-green-100 text-green-800 border-green-300"
    };
    return colors[level] || "bg-gray-100 text-gray-800";
  };

  const CalculatorContent = () => (
    <div className="space-y-6">
      {/* Room Count & Risk Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="roomCount" className="flex items-center gap-2">
            <Hotel className="w-4 h-4" />
            Oda Sayısı
          </Label>
          <Input
            id="roomCount"
            type="number"
            placeholder="Örn: 150"
            value={roomCount}
            onChange={(e) => setRoomCount(e.target.value)}
            min="1"
            data-testid="room-count-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="riskLevel" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Risk Seviyesi
          </Label>
          <Select value={riskLevel} onValueChange={setRiskLevel}>
            <SelectTrigger data-testid="risk-level-select">
              <SelectValue placeholder="Risk seviyesi seçin" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high_risk">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Yüksek Risk
                </span>
              </SelectItem>
              <SelectItem value="low_risk">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  Düşük Risk
                </span>
              </SelectItem>
              <SelectItem value="extremely_low_risk">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Çok Düşük Risk
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Extremely Low Risk Criteria Notice */}
      {riskLevel === "extremely_low_risk" && (
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">Çok Düşük Risk Kriterleri</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• 20'den az oda ve 15'ten az çalışan</li>
                <li>• Konferans/toplantı salonu yok</li>
                <li>• Yerel mülkiyet (çoklu site hariç)</li>
                <li>• Hassas alanlarda değil</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                Not: 100+ oda için çok düşük risk uygulanamaz
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Factors */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Risk Faktörleri (+0.10 A/G her biri için)
          </Label>
          <Badge variant="outline" className="text-xs">
            Seçili: {selectedFactors.length}
          </Badge>
        </div>
        
        {loadingFactors ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto border rounded-lg p-3 space-y-2">
            {riskFactors.map((factor) => (
              <div
                key={factor.id}
                className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                  selectedFactors.includes(factor.id) 
                    ? "bg-[#E8F3EE] border border-[#1A4D2E]/30" 
                    : "hover:bg-gray-50"
                }`}
                onClick={() => toggleFactor(factor.id)}
                data-testid={`risk-factor-${factor.id}`}
              >
                <Checkbox
                  checked={selectedFactors.includes(factor.id)}
                  onCheckedChange={() => toggleFactor(factor.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{factor.label}</p>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">
                  +{factor.increment} A/G
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Calculate Button */}
      <Button 
        onClick={calculateManDay} 
        disabled={loading || !roomCount}
        className="w-full bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
        data-testid="calculate-manday-btn"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Hesaplanıyor...
          </>
        ) : (
          <>
            <Calculator className="w-4 h-4 mr-2" />
            Adam-Gün Hesapla
          </>
        )}
      </Button>

      {/* Results */}
      {calculationResult && (
        <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-[#E8F3EE] to-[#F0F9F4] border border-[#1A4D2E]/20">
          <h4 className="font-semibold text-[#1A4D2E] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Hesaplama Sonucu (TL.01)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-3 rounded-lg bg-white border">
              <p className="text-xs text-gray-500 mb-1">İlk Belgelendirme</p>
              <p className="text-2xl font-bold text-[#1A4D2E]">
                {calculationResult.initial} <span className="text-sm font-normal">A/G</span>
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white border">
              <p className="text-xs text-gray-500 mb-1">Gözetim Denetimi</p>
              <p className="text-2xl font-bold text-blue-600">
                {calculationResult.surveillance} <span className="text-sm font-normal">A/G</span>
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white border">
              <p className="text-xs text-gray-500 mb-1">Yeniden Belgelendirme</p>
              <p className="text-2xl font-bold text-purple-600">
                {calculationResult.recertification} <span className="text-sm font-normal">A/G</span>
              </p>
            </div>
          </div>

          {/* Breakdown */}
          <div className="text-sm space-y-2 p-3 rounded-lg bg-white/50">
            <div className="flex justify-between">
              <span className="text-gray-600">Oda Kategorisi:</span>
              <span className="font-medium">{calculationResult.room_category} oda</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Risk Seviyesi:</span>
              <Badge className={getRiskLevelColor(calculationResult.risk_level)}>
                {getRiskLevelLabel(calculationResult.risk_level)}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Tablo Değeri:</span>
              <span className="font-medium">{calculationResult.base_manday} A/G</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Risk Faktör Artışı:</span>
              <span className="font-medium text-orange-600">
                +{calculationResult.risk_increment} A/G 
                ({calculationResult.breakdown?.risk_factors_count || 0} faktör)
              </span>
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between">
              <span className="font-semibold text-gray-700">Toplam:</span>
              <span className="font-bold text-[#1A4D2E]">{calculationResult.initial} A/G</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Show as card directly
  if (showAsCard) {
    return (
      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Calculator className="w-5 h-5" />
            Adam-Gün Hesaplama (TL.01)
          </CardTitle>
          <CardDescription>
            GSTC denetim süresi hesaplayıcı - oda sayısı ve risk faktörlerine göre
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CalculatorContent />
        </CardContent>
      </Card>
    );
  }

  // Show as dialog with trigger button
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button 
            variant="outline" 
            className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F3EE]"
            data-testid="open-manday-calculator-btn"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Adam-Gün Hesapla
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Calculator className="w-5 h-5" />
            Adam-Gün Hesaplama (TL.01)
          </DialogTitle>
          <DialogDescription>
            GSTC denetim süresi hesaplayıcı - oda sayısı ve risk faktörlerine göre
          </DialogDescription>
        </DialogHeader>
        
        <CalculatorContent />

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManDayCalculator;
